/**
 * Panel Instance — one dockable SidePanelCore driven entirely by the
 * registry.
 *
 * This is the generic replacement for the per-app "SidePanel wrapper": it
 * owns the instance's group state (persisted under the panel storage
 * namespace), re-resolves tab icon/content through the registry on every
 * restore, and speaks the namespaced `panel:*` event protocol (toggle,
 * restore, visibility sync, workspace apply/reset/capture). Applications
 * declare panels; this component runs the shell around them.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SidePanelCore } from './SidePanelCore';
import { PanelGroup, TabConfig, FloatingWindow } from './types';
import {
    clearHiddenPanels,
    isPanelHidden,
    isStaleHidden,
    markPanelVisible,
} from './panelVisibilityFlags';
import { useSidePanelStore } from '../store/useSidePanelStore';
import { useWorkspaceStore, WorkspaceGroupSpec } from '../store/useWorkspaceStore';
import { panelStorageKey } from '../store/namespace';
import { PanelRegistry } from '../registry/createPanelRegistry';
import { PANEL_EVENTS } from './protocol';
import { DockTarget } from '../store/useDockLayoutStore';

const DEFAULT_GROUP_HEIGHT = 200;

export interface PanelInstanceProps {
    registry: PanelRegistry;
    panelId: string;
    panelPosition: 'left' | 'right' | 'top' | 'bottom';
    /** Position inside the side's dock strip (useDockLayoutStore order) */
    dockIndex?: number;
    setPanelPosition: (pos: 'left' | 'right' | 'top' | 'bottom') => void;
    /** Full side+index dock decision from the shared drag resolver */
    onDockTarget?: (target: DockTarget) => void;
    onAddPanel: (
        position: 'left' | 'right' | 'top' | 'bottom',
        panelId?: string,
        tab?: TabConfig,
    ) => string;
    onRemovePanel: (panelId: string) => void;
    floatingWindows?: FloatingWindow[];
    setFloatingWindows?: React.Dispatch<React.SetStateAction<FloatingWindow[]>>;
}

const panelGroupsStorageKey = (panelId: string): string =>
    panelStorageKey(`panelGroups.${panelId}`);

/** Resolve a canonical (unprefixed) tab id to its registry definition. */
const resolveTabDefinition = (
    registry: PanelRegistry,
    panelId: string,
    tabId: string,
) => {
    // Strip the instance prefix: ids are spelled `${panelId}-${canonicalId}`,
    // and the canonical id itself is the longest registered suffix.
    const stripped = tabId.startsWith(`${panelId}-`)
        ? tabId.slice(panelId.length + 1)
        : tabId;
    const canonical = registry.toCanonicalId(stripped);
    const definition = registry.get(canonical);
    if (canonical !== stripped) registry.alias(stripped, canonical);
    return definition;
};

export const PanelInstance: React.FC<PanelInstanceProps> = ({
    registry,
    panelId,
    panelPosition,
    dockIndex = 0,
    setPanelPosition,
    onDockTarget,
    onAddPanel,
    onRemovePanel,
    floatingWindows = [],
    setFloatingWindows,
}) => {
    // Restore/resize-driven re-creation of tabs is owned by the primary
    // shell. Dynamic instances (spawned when floating a panel out) must not
    // answer toggle/restore broadcasts, otherwise a closed panel is rebuilt
    // twice — once per mounted instance — and dynamic shells keep
    // resurrecting themselves even after a workspace reset.
    const isPrimaryPanel = panelId === 'main-panel';

    const buildGroups = useCallback(
        (specs: ReturnType<PanelRegistry['getDefaultGroups']>): PanelGroup[] =>
            specs.map((groupSpec) => ({
                id: `${panelId}-${groupSpec.id}`,
                isVisible: true,
                height: DEFAULT_GROUP_HEIGHT,
                isMinimized: false,
                tabs: groupSpec.tabs
                    .map((tabSpec): TabConfig | null => {
                        const spec =
                            typeof tabSpec === 'string'
                                ? { id: tabSpec }
                                : tabSpec;
                        const definition = registry.get(spec.id);
                        if (!definition || !definition.render) return null;
                        return {
                            id: `${panelId}-${spec.id}`,
                            label: spec.label ?? definition.label,
                            labelKey:
                                spec.labelKey ?? definition.labelKey,
                            icon: spec.icon ?? definition.icon,
                            content: definition.render(),
                        };
                    })
                    .filter((tab): tab is TabConfig => tab !== null),
            }))
            .filter((group) => group.tabs.length > 0),
        [registry, panelId],
    );

    // Restore persisted groups with fresh icon/content resolution, or fall
    // back to the registry's factory layout.
    const initialPanelGroups = useRef<PanelGroup[]>(
        null as unknown as PanelGroup[],
    );
    if (initialPanelGroups.current === null) {
        let stored: PanelGroup[] | null = null;
        if (typeof window !== 'undefined') {
            try {
                const raw = localStorage.getItem(panelGroupsStorageKey(panelId));
                if (raw) stored = JSON.parse(raw);
            } catch {
                stored = null;
            }
        }
        initialPanelGroups.current = stored
            ? stored
                  .map((group) => ({
                      ...group,
                      tabs: group.tabs
                          .map((tab): TabConfig | null => {
                              const definition = resolveTabDefinition(
                                  registry,
                                  panelId,
                                  tab.id,
                              );
                              if (!definition || !definition.render) return null;
                              return {
                                  ...tab,
                                  icon: tab.icon ?? definition.icon,
                                  content: definition.render(),
                              };
                          })
                          .filter((tab): tab is TabConfig => tab !== null),
                  }))
                  .filter((group) => group.tabs.length > 0)
            : buildGroups(registry.getDefaultGroups(panelId));
    }

    const [panelGroups, setPanelGroups] =
        useState<PanelGroup[]>(initialPanelGroups.current);

    const canonicalOf = (tabId: string): string => {
        const stripped = tabId.startsWith(`${panelId}-`)
            ? tabId.slice(panelId.length + 1)
            : tabId;
        return registry.toCanonicalId(stripped);
    };

    // Keep the zustand visibility store in sync on every layout change so
    // menu checkmarks are always current — including the very first render,
    // before any user interaction.
    useEffect(() => {
        const next = new Set<string>();
        panelGroups
            .filter((group) => group.isVisible)
            .forEach((group) =>
                group.tabs.forEach((tab) => {
                    if (!isPanelHidden(tab.id)) {
                        next.add(canonicalOf(tab.id));
                    }
                }),
            );
        useSidePanelStore.getState().syncVisiblePanels([...next]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panelGroups]);

    // Broadcast initial visibility so other consumers learn the
    // restored/default layout on first open.
    useEffect(() => {
        const timer = setTimeout(() => {
            panelGroups
                .filter((group) => group.isVisible)
                .flatMap((group) =>
                    group.tabs.filter((tab) => !isPanelHidden(tab.id)),
                )
                .forEach((tab) => {
                    window.dispatchEvent(
                        new CustomEvent(PANEL_EVENTS.visibilityChange, {
                            detail: { panelId: canonicalOf(tab.id), visible: true },
                        }),
                    );
                });
        }, 0);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist the layout on every change (icon/content stripped — they are
    // React nodes).
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const groupsToSave = panelGroups.map((group) => ({
                ...group,
                tabs: group.tabs.map((tab) => {
                    const { icon: _icon, content: _content, ...tabData } = tab;
                    return tabData;
                }),
            }));
            localStorage.setItem(
                panelGroupsStorageKey(panelId),
                JSON.stringify(groupsToSave),
            );
        } catch {
            // Storage unavailable: layout stays in-memory only
        }
    }, [panelGroups, panelId]);

    // Mirror canonical visibility changes into the zustand store so menu
    // checkmarks read one consistent source of truth.
    useEffect(() => {
        const handleVisibilityChange = (event: Event) => {
            const customEvent = event as CustomEvent<{
                panelId: string;
                visible?: boolean;
            }>;
            if (!customEvent.detail?.panelId) return;
            useSidePanelStore
                .getState()
                .setPanelVisible(
                    customEvent.detail.panelId,
                    customEvent.detail.visible !== false,
                );
        };

        const handleRequestVisibility = () => {
            panelGroups
                .filter((group) => group.isVisible)
                .flatMap((group) =>
                    group.tabs.filter((tab) => !isPanelHidden(tab.id)),
                )
                .forEach((tab) => {
                    window.dispatchEvent(
                        new CustomEvent(PANEL_EVENTS.visibilityChange, {
                            detail: { panelId: canonicalOf(tab.id), visible: true },
                        }),
                    );
                });
        };

        window.addEventListener(
            PANEL_EVENTS.visibilityChange,
            handleVisibilityChange,
        );
        window.addEventListener(
            PANEL_EVENTS.requestVisibility,
            handleRequestVisibility,
        );
        return () => {
            window.removeEventListener(
                PANEL_EVENTS.visibilityChange,
                handleVisibilityChange,
            );
            window.removeEventListener(
                PANEL_EVENTS.requestVisibility,
                handleRequestVisibility,
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panelGroups, panelId]);

    const restoreTab = useMemo(
        () =>
            (requestedPanelId: string): void => {
                const defaults = buildGroups(registry.getDefaultGroups(panelId));
                // The requested id may be a menu-level alias; canonicalize it
                // and match against the instance-prefixed tab ids.
                const targetCanonical = registry.toCanonicalId(requestedPanelId);

                const isMatch = (tabId: string) =>
                    tabId === requestedPanelId ||
                    canonicalOf(tabId) === targetCanonical;

                const defaultGroup = defaults.find((g) =>
                    g.tabs.some((t) => isMatch(t.id)),
                );
                if (!defaultGroup) return;
                const defaultTab = defaultGroup.tabs.find((t) => isMatch(t.id));
                if (!defaultTab) return;

                // Clear the hidden flag BEFORE re-adding the tab: the shell's
                // deferred restore check reads it in the same event cycle and
                // must not treat the panel it is about to show as still hidden.
                markPanelVisible(defaultTab.id);

                setPanelGroups((prev) => {
                    const existingGroupIndex = prev.findIndex((g) =>
                        g.tabs.some((t) => isMatch(t.id)),
                    );
                    if (existingGroupIndex !== -1) {
                        if (prev[existingGroupIndex].isVisible) return prev;
                        return prev.map((g, idx) =>
                            idx === existingGroupIndex
                                ? { ...g, isVisible: true }
                                : g,
                        );
                    }

                    // Group survived without the tab: add it back there
                    const matchingGroupId = prev.findIndex(
                        (g) => g.id === defaultGroup.id,
                    );
                    if (matchingGroupId !== -1) {
                        return prev.map((g, idx) =>
                            idx === matchingGroupId
                                ? {
                                      ...g,
                                      isVisible: true,
                                      tabs: [...g.tabs, defaultTab],
                                  }
                                : g,
                        );
                    }

                    // Prepend so a restored panel whose original group no
                    // longer exists shows up on TOP of the stack.
                    return [
                        { ...defaultGroup, isVisible: true, tabs: [defaultTab] },
                        ...prev,
                    ];
                });

                // Activate (not toggle) so the deferred call can only show the
                // restored tab, never toggle a just-opened one off.
                setTimeout(() => {
                    window.dispatchEvent(
                        new CustomEvent(PANEL_EVENTS.activateTab, {
                            detail: { panelId: defaultTab.id },
                        }),
                    );
                }, 50);
            },
        [buildGroups, registry, panelId],
    );

    // Toggle / restore protocol — primary shell only.
    useEffect(() => {
        const handleRestoreTab = (event: Event) => {
            if (!isPrimaryPanel) return;
            const customEvent = event as CustomEvent<{ panelId: string }>;
            if (!customEvent.detail?.panelId) return;
            const targetId = customEvent.detail.panelId;
            // Defer past the current event tick and re-check: the instance
            // that owns this panel may be hiding it in this same toggle cycle.
            setTimeout(() => {
                if (!isPanelHidden(targetId) || isStaleHidden(targetId)) {
                    restoreTab(targetId);
                }
            }, 0);
        };

        const handleTogglePanel = (event: Event) => {
            if (!isPrimaryPanel) return;
            const customEvent = event as CustomEvent<{ panelId: string }>;
            const targetId = customEvent.detail?.panelId;
            if (!targetId) return;

            if (panelGroups.length === 0) {
                setPanelGroups(buildGroups(registry.getDefaultGroups(panelId)));
                setTimeout(() => {
                    window.dispatchEvent(
                        new CustomEvent(PANEL_EVENTS.activateTab, {
                            detail: { panelId: targetId },
                        }),
                    );
                }, 50);
                return;
            }

            // Group ids are never toggled
            const isGroup = panelGroups.some(
                (g) => g.id === targetId || g.id.endsWith(`-${targetId}`),
            );
            if (isGroup) return;

            const tabExists = panelGroups.some((g) =>
                g.tabs.some((t) => canonicalOf(t.id) === registry.toCanonicalId(targetId)),
            );
            const visibleElsewhere = useSidePanelStore
                .getState()
                .visiblePanels.has(targetId);

            if (
                !tabExists &&
                !visibleElsewhere &&
                (!isPanelHidden(targetId) || isStaleHidden(targetId))
            ) {
                setTimeout(() => {
                    if (!isPanelHidden(targetId) || isStaleHidden(targetId)) {
                        restoreTab(targetId);
                    }
                }, 0);
            }
        };

        window.addEventListener(PANEL_EVENTS.requestRestoreTab, handleRestoreTab);
        window.addEventListener(PANEL_EVENTS.togglePanel, handleTogglePanel);
        return () => {
            window.removeEventListener(
                PANEL_EVENTS.requestRestoreTab,
                handleRestoreTab,
            );
            window.removeEventListener(
                PANEL_EVENTS.togglePanel,
                handleTogglePanel,
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panelGroups, panelId, isPrimaryPanel, restoreTab, buildGroups, registry]);

    // Workspace apply/reset — primary shell only. Custom (user-saved)
    // workspaces win over the registry's factory presets.
    useEffect(() => {
        if (!isPrimaryPanel) return;

        const applyGroups = (groups: PanelGroup[], activeId: string) => {
            clearHiddenPanels();
            setPanelGroups(groups);
            useSidePanelStore.getState().clearVisiblePanels();
            useWorkspaceStore.getState().setActiveWorkspace(activeId);
            // Re-sync checkmarks with the fresh layout.
            setTimeout(() => {
                window.dispatchEvent(
                    new CustomEvent(PANEL_EVENTS.requestVisibility),
                );
            }, 0);
        };

        const buildFromSpecs = (specs: WorkspaceGroupSpec[]): PanelGroup[] =>
            specs.map((groupSpec) => ({
                id: `${panelId}-${groupSpec.id}`,
                isVisible: true,
                height: DEFAULT_GROUP_HEIGHT,
                isMinimized: false,
                tabs: groupSpec.tabs
                    .map((tabSpec): TabConfig | null => {
                        const definition = registry.get(tabSpec.id);
                        if (!definition || !definition.render) return null;
                        return {
                            id: `${panelId}-${tabSpec.id}`,
                            label: tabSpec.label ?? definition.label,
                            icon: definition.icon,
                            content: definition.render(),
                        };
                    })
                    .filter((tab): tab is TabConfig => tab !== null),
            }));

        const handleApplyWorkspace = (event: Event) => {
            const customEvent = event as CustomEvent<{ workspaceId?: string }>;
            const workspaceId = customEvent.detail?.workspaceId;
            if (!workspaceId) return;

            const custom = useWorkspaceStore
                .getState()
                .getCustomWorkspace(workspaceId);
            if (custom) {
                if (!custom.capturePanelLocations) {
                    useWorkspaceStore.getState().setActiveWorkspace(custom.id);
                    return;
                }
                applyGroups(buildFromSpecs(custom.groups), custom.id);
            }
        };

        const handleResetWorkspace = () => {
            applyGroups(
                buildGroups(registry.getDefaultGroups(panelId)),
                registry.defaultWorkspaceId,
            );
        };

        const handleCaptureWorkspace = () => {
            const groups = panelGroups.map((group) => ({
                id: group.id.replace(`${panelId}-`, ''),
                tabs: group.tabs.map((tab) => ({
                    id: canonicalOf(tab.id),
                    label: tab.label,
                })),
            }));
            window.dispatchEvent(
                new CustomEvent(PANEL_EVENTS.captureWorkspaceResult, {
                    detail: { groups },
                }),
            );
        };

        // Drop hidden flags when a workspace is applied/reset so toggling
        // works from a clean state with the fresh group layout.
        const handleLayoutRefresh = () => {
            clearHiddenPanels();
            useSidePanelStore.getState().clearVisiblePanels();
            window.dispatchEvent(
                new CustomEvent(PANEL_EVENTS.requestVisibility),
            );
        };

        window.addEventListener(PANEL_EVENTS.applyWorkspace, handleApplyWorkspace);
        window.addEventListener(PANEL_EVENTS.resetWorkspace, handleResetWorkspace);
        window.addEventListener(
            PANEL_EVENTS.captureWorkspace,
            handleCaptureWorkspace,
        );
        window.addEventListener(PANEL_EVENTS.applyWorkspace, handleLayoutRefresh);
        window.addEventListener(PANEL_EVENTS.resetWorkspace, handleLayoutRefresh);
        return () => {
            window.removeEventListener(
                PANEL_EVENTS.applyWorkspace,
                handleApplyWorkspace,
            );
            window.removeEventListener(
                PANEL_EVENTS.resetWorkspace,
                handleResetWorkspace,
            );
            window.removeEventListener(
                PANEL_EVENTS.captureWorkspace,
                handleCaptureWorkspace,
            );
            window.removeEventListener(
                PANEL_EVENTS.applyWorkspace,
                handleLayoutRefresh,
            );
            window.removeEventListener(
                PANEL_EVENTS.resetWorkspace,
                handleLayoutRefresh,
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panelGroups, panelId, isPrimaryPanel, buildGroups, registry]);

    const onDockTargetProxy = (target: DockTarget) => {
        setPanelPosition(target.side);
        onDockTarget?.(target);
    };

    return (
        <SidePanelCore
            panelId={panelId}
            defaultHeight={DEFAULT_GROUP_HEIGHT}
            panelPosition={panelPosition}
            dockIndex={dockIndex}
            setPanelPosition={setPanelPosition}
            onDockTarget={onDockTargetProxy}
            onAddPanel={onAddPanel}
            onRemovePanel={onRemovePanel}
            floatingWindows={floatingWindows}
            setFloatingWindows={setFloatingWindows}
            setPanelGroups={setPanelGroups}
            panelGroups={panelGroups}
        />
    );
};
